import '../styles/Map.scss';
import '../components/Widgets/Widgets.scss';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { SearchBox } from '@mapbox/search-js-react';
import Sidebar from './Sidebar';
import axios from 'axios';
import Loading from './Loading';
import Error from './Error';
import MapWidgets from './Widgets/MapWidgets.js';
import MapWeatherWidgets from './Widgets/MapWeatherWidgets.js';
import Spinner from './Spinner.js';
import loadingGif from '../assets/icons/loading.gif';
import locationIcon from '../assets/icons/location-icon.png';
import { useFiresContext } from '../contexts/FiresContext.js';
import { usePanelContext } from '../contexts/PanelContext.js';
import { useAlertsContext } from '../contexts/AlertsContext.js';
import { useAreasContext } from '../contexts/AreasContext.js';
import { useSensorDataContext } from '../contexts/SensorDataContext.js';
import { useFacilitiesContext } from '../contexts/FacilitiesContext.js';
import { useUserContext } from '../contexts/UserContext.js';
import { useTeamsContext } from '../contexts/TeamsContext.js';
import cursorManager from '../utils/cursorManager.js';
import TerrainPopup from './panels/Fires/TerrainPopUp.js';

// Set Mapbox access token - CRITICAL for map initialization
if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) {
  console.error('REACT_APP_MAPBOX_ACCESS_TOKEN is not set! The map will not work properly.');
  console.error('Please set this environment variable in your build environment (AWS Amplify Console > App settings > Environment variables)');
}
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

export default function MapView({ user, setUser }) {
  // refs
  const container = useRef(null);
  const map = useRef(null);

  // states
  const [userLoc, setUserLoc] = useState([]);
  const [areasLoading, setAreasLoading] = useState(true);
  const [spinning, setSpinning] = useState(true);

  // contexts
  const { getFires } = useFiresContext();
  const { panel, setPanel } = usePanelContext();
  const { getAlerts } = useAlertsContext();
  const { getAreas, setTeamAreas, setMyAreas, teamAreas, myAreas } = useAreasContext();
  const { getPredictions, setPredictions, getRAWS, getVIIRS, getMODIS, plotFlights, plotSatellites, getSatelliteLayers, satelliteLayers, setSatelliteLayers, selectedSatelliteLayer, setSelectedSatelliteLayer, flights, satellites } = useSensorDataContext();
  const { getPowerPlants, getEmergencyStations } = useFacilitiesContext();
  const { getTeams, setTeams } = useTeamsContext();
  const { config, setConfig, getToken } = useUserContext();

  // gelocation set-up
  useEffect(() => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => setUserLoc([pos.coords.longitude, pos.coords.latitude]),
      err => alert(err.message)
    );
  }, []);

  // track loading of PIF's (areas)
  useEffect(() => setAreasLoading(!(teamAreas && myAreas)), [teamAreas, myAreas]);

  // Note: Fires, VIIRS, and MODIS are now loaded in the map.style.load event handler
  // to ensure the map is fully initialized before plotting

  // pull data (auth token required)
  useEffect(() => {
    if (!user) {
      setTeamAreas([]); setMyAreas([]);
      setPredictions([]); setTeams([]);
      return;
    }
    getToken().then(token => {
      getAreas(map, token);
      getPredictions(map, token);
      getAlerts(token);
      getSatelliteLayers(token);
      getTeams(token);
    });
    const t = setTimeout(() => getRAWS(config, map, user), 500);
    return () => clearTimeout(t);
  }, [user]);

  // update user config settings (map style)
  useEffect(() => {
    if (config && config !== 'error' && map.current) {
      axios.post(
        'https://firewatch-api.space-eyes.com/user/settings',
        { settings: config },
        { headers: { Authorization: `Bearer ${user.idToken.jwtToken}` } }
      ).catch(() => setConfig('error'));
    }
  }, [config]);

  // map init
  useEffect(() => {
    if (!config || config === 'error' || map.current) return;
    
    // Check if Mapbox token is set
    if (!mapboxgl.accessToken) {
      console.error('Mapbox access token is missing. Cannot initialize map.');
      return;
    }
    
    try {
      map.current = new mapboxgl.Map({
        container: container.current,
        style: config.map_style,
        center: [-97, 38], zoom: 1, bearing: 0, pitch: 0,
        antialias: true, attributionControl: false
      });
    } catch (error) {
      console.error('Failed to initialize Mapbox map:', error);
      return;
    }

    map.current.addControl(new mapboxgl.NavigationControl());

    // Reset cursor state when map is initialized
    cursorManager.forceResetAll(map);

    // Wait for map to be fully loaded before adding layers
    let dataInitialized = false;
    const initializeMapData = () => {
      if (dataInitialized) {
        console.log('Data already initialized, skipping...');
        return;
      }
      dataInitialized = true;
      console.log('Map initialized, loading data...');
      console.log('Map loaded:', map.current.loaded());
      console.log('Style loaded:', map.current.isStyleLoaded());
      
      // adding 3d terrain, buildings, etc
      try {
        const layers = map.current.getStyle().layers;
        const labelLayerId = layers.find(l => l.type==='symbol' && l.layout['text-field']).id;
        map.current.addLayer({ id:'3d-buildings', source:'composite', 'source-layer':'building', filter:['==','extrude','true'], type:'fill-extrusion', minzoom:15, paint:{ 'fill-extrusion-color':'#aaa', 'fill-extrusion-height':['interpolate',['linear'],['zoom'],15,0,15.05,['get','height']], 'fill-extrusion-base':['interpolate',['linear'],['zoom'],15,0,15.05,['get','min_height']], 'fill-extrusion-opacity':0.8 }
        }, labelLayerId);

        map.current.addSource('dem',{ type:'raster-dem', url:'mapbox://mapbox.mapbox-terrain-dem-v1', tileSize:512, maxzoom:14 });
        map.current.setTerrain({ source:'dem', exaggeration:1 });
        console.log('3D terrain and buildings added');
      } catch (error) {
        console.error('Error adding 3D terrain/buildings:', error);
      }

      // pull more data (these needed to be pulled after style.load for some reason)
      try {
        console.log('Loading power plants and emergency stations...');
        getPowerPlants(map); 
        getEmergencyStations(map);
      } catch (error) {
        console.error('Error loading power plants/emergency stations:', error);
      }
      
      // Load fires, VIIRS, and MODIS after map is ready (no auth required)
      try {
        console.log('Loading fires...');
        getFires(map);
      } catch (error) {
        console.error('Error loading fires:', error);
      }
      
      setTimeout(() => { 
        try {
          console.log('Loading VIIRS and MODIS...');
          getVIIRS(map); 
          getMODIS(map);
        } catch (error) {
          console.error('Error loading VIIRS/MODIS:', error);
        }
      }, 500);
      
      // Load flights and satellites (auth required)
      getToken().then(t => {
        try {
          console.log('Loading flights and satellites...');
          plotFlights(map,t); 
          plotSatellites(map,t);
        } catch (error) {
          console.error('Error loading flights/satellites:', error);
        }
      }).catch(error => {
        console.error('Error getting token for flights/satellites:', error);
      });
    };

    map.current.on('style.load', () => {
      map.current.setProjection({ name: 'globe' });

      // Check if we should skip globe spinning (e.g., during programmatic style changes)
      if (map.current._skipGlobeSpin) {
        map.current._skipGlobeSpin = false; // Reset the flag
        setSpinning(false); // Set spinning to false immediately
        initializeMapData();
      } else {
        // globe spin logic (only for initial load)
        let spin = true, interact = false; let iv = null;
        ['mousedown','dragstart','rotatestart','zoomstart'].forEach(evt => map.current.on(evt, () => { interact = true; spin = false; clearInterval(iv); }));
        const spinGlobe = () => {
          const secs = 20, delta = 360 / secs;
          iv = setInterval(() => {
            if (!spin || interact) return clearInterval(iv);
            const c = map.current.getCenter();
            map.current.easeTo({ center: [c.lng - delta, c.lat], pitch: 0, duration: 1000, easing: t => t });
          }, 1000);
        };
        spinGlobe();

        setTimeout(() => {
          spin = false; clearInterval(iv); setSpinning(false);
          map.current.flyTo({ center: [-114.2426, 44.0549], zoom: 4, pitch: 65, duration: 5000 });
          initializeMapData();
        }, 7000);
      }
    });

    // Also listen for load event as backup (fires after all tiles are loaded)
    map.current.once('load', () => {
      console.log('Map load event fired');
      if (!dataInitialized) {
        console.log('Initializing data from load event (backup)...');
        setTimeout(() => {
          initializeMapData();
        }, 100);
      }
    });
  }, []);

  // use current location
  const flyToUser = useCallback(() => {
    if (userLoc.length>1) map.current.flyTo({ center:userLoc, zoom:11, pitch:0, offset:[250,0] });
  }, [userLoc]);

  // wkt string to rings function
  const WKTtoRings = useCallback(w => w.slice(9,-2).split(',').map(s=>s.trim().split(' ').map(Number)), []);

  // return name for search location
  const returnName = useCallback(d => d.full_address||d.address||d.name||'Unknown', []);

  // centroid from rings
  const getCentroid = useCallback(r=>{
    if(r[0][0]!=r[r.length-1][0]||r[0][1]!=r[r.length-1][1]) r.push(r[0]);
    let A=0,X=0,Y=0,j=r.length-1;
    for(let i=0;i<r.length;j=i++){ const [x1,y1]=r[i], [x2,y2]=r[j],f=x1*y2-x2*y1;A+=f;X+=(x1+x2)*f;Y+=(y1+y2)*f;}       
    return [X/(3*A),Y/(3*A)];
  }, []);

  const selectedLocation = {
    "lat": 43.230383,
    "lon": -106.408768,
    "popupPosition": {
        "x": 755.9999999999374,
        "y": 381.4999999991365
    },
    "areaLengthKm": "39.1",
    "areaWidthKm": "28.5",
    "areaSizeKm2": "1115.9"
 }

  return (
    <div className="map">
      {config === 'error' ? <Error/>:!config?<Loading/> : (
        <>
          <Sidebar getPredictions={getPredictions} panel={panel} setPanel={setPanel} user={user} setUser={setUser} config={config} setConfig={setConfig} map={map}/>
          <MapWidgets map={map} WKTtoRings={WKTtoRings} getCentroid={getCentroid} panel={panel} setPanel={setPanel}/>
          <MapWeatherWidgets map={map} panel={panel} setPanel={setPanel} satelliteLayers={satelliteLayers} setSatelliteLayers={setSatelliteLayers} selectedSatelliteLayer={selectedSatelliteLayer} setSelectedSatelliteLayer={setSelectedSatelliteLayer} satellites={satellites} flights={flights}/>
          <div className='map-search'>
            <SearchBox accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN} placeholder='Search Location'
                onRetrieve={ e => { 
                    try {
                        map.current.flyTo({center: e.features[0].geometry.coordinates, zoom:11, pitch:0, offset:[250,0]});
                        setPanel({name:'Searched Location', content: { coordinates:e.features[0].geometry.coordinates, address: returnName(e.features[0].properties) }});
                    } catch{}}}
            />
          </div>
          <div className='map-current-location-btn' onClick={flyToUser}>
            <img src={locationIcon} className='map-current-location-btn-img'/>
            <div className='map-current-location-btn-text'>Use Current Location</div>
          </div>
          {user && areasLoading && 
            <div style={{ zIndex: 1, position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', color: 'white', background: 'rgba(128, 128, 128, 0.21)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem', borderRadius: 10 }}>
                <div style={{ fontSize: '0.9rem', color: 'white', fontWeight: 'bold' }}>Your areas are still loading in...</div>
                <img style={{ width: '1.5rem' }} src={loadingGif}/>
            </div>}
          {spinning && <div style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', zIndex:6 }}/>}
          <div ref={container} className="map-view"/>
        </>
      )}
    </div>
  );
}
